/**
 * The automation's output settings for the settings panel, one
 * `SettingSection` each:
 * 1. target  — target tables (write permission only; the source table is a
 *    normal target unless status tracking deletes processed rows)
 * 2. fields  — which response field is written to which column
 * 3. match   — "Zeilen schreiben": new row, update, or update-or-create,
 *    and the columns an existing row is recognised by
 * 4. fanout  — one row per entry of a list in the response
 *
 * With several target tables, each setting shows one part per table.
 */

import { notify, Switch } from '@affine/component';
import { UserSettingsService } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  AutomationConfig,
  MatchConfigProps,
  OutputMapping,
  OutputTable,
} from '../services/automation';
import type { ColumnInfo } from '../services/database-discovery';
import { WorkflowService } from '../services/workflow';
import { TutorialRegistry } from '../tutorial/services/tutorial-registry';
import {
  isSourceRowTable,
  updatesSourceRow,
} from './automation-input-requirements';
import { CollapsibleSection } from './components/collapsible-section';
import { FanoutExample } from './components/fanout-example';
import { FieldMappingGrid } from './components/field-mapping-grid';
import { useOpenWorkflow } from './components/open-workflow';
import { useOutputTableActions } from './components/output-table-actions';
import type { RowWriteState } from './components/row-write';
import { RowWriteSection } from './components/row-write-section';
import {
  SectionAddLink,
  SectionControlRow,
  SectionField,
  SectionHint,
  SectionRemoveButton,
  SectionTableGroup,
  SectionTableRow,
} from './components/section-layout';
import { SettingSection } from './components/setting-section';
import { SettingSelect } from './components/setting-select';
import { TableSelector } from './components/table-selector';

export interface OutputTabProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Target table columns (for mapping) - used for single table mode */
  targetColumns?: ColumnInfo[];
  /** sl-3nk: Map of tableId -> columns for multi-table field mapping */
  tableColumnsMap?: Map<string, ColumnInfo[]>;
  /** Source table columns (for resolving column IDs to names) */
  sourceColumns?: ColumnInfo[];
  /** sl-3nk: Only owner can configure AI relation matching */
  isOwner?: boolean;
}

const NO_COLUMNS: ColumnInfo[] = [];
const NO_TABLES: OutputTable[] = [];
const NO_MAPPINGS: OutputMapping[] = [];
const NO_MATCH_CONFIGS: MatchConfigProps[] = [];

/** Whether a response field of this type can hold a list. */
function canHoldList(type: string): boolean {
  // A list leaves the workflow as an array or as a JSON field (the End
  // editor has no array type); the output writer checks at run time that the
  // value is an array.
  const normalized = type.toLowerCase();
  return (
    normalized === 'json' ||
    normalized === 'array' ||
    normalized.endsWith('[]') ||
    normalized.startsWith('array')
  );
}

export const OutputTab: FC<OutputTabProps> = ({
  config,
  onUpdate,
  targetColumns = NO_COLUMNS,
  tableColumnsMap,
  sourceColumns,
  isOwner = false,
}) => {
  const t = useI18n();
  // sl-3nk: User settings for AI relation matching toggle
  const userSettingsService = useService(UserSettingsService);
  const userSettings = useLiveData(userSettingsService.userSettings$);
  const [isUpdatingAiSetting, setIsUpdatingAiSetting] = useState(false);

  const mappings = config.outputMappings ?? NO_MAPPINGS;
  const matchConfigs = config.matchConfigs ?? NO_MATCH_CONFIGS;
  const savedTables = config.outputTables ?? NO_TABLES;

  // The AI switch belongs to relation lookups (mappings with a match config).
  const hasRelationLookup = mappings.some(mapping => mapping.matchConfigId);

  const handleAiMatchingToggle = useCallback(
    (enabled: boolean) => {
      setIsUpdatingAiSetting(true);
      userSettingsService
        .updateUserSettings({ aiRelationMatchingEnabled: enabled })
        .catch(() => {
          notify.error({
            title: t['com.streamlain.streamlain.output.ai-matching-failed'](),
          });
        })
        .finally(() => {
          setIsUpdatingAiSetting(false);
        });
    },
    [userSettingsService, t]
  );

  const registry = useService(TutorialRegistry);
  const workflowService = useService(WorkflowService);
  const workflowSchema = useLiveData(workflowService.selectedWorkflowSchema$);
  const schemaLoadingMap = useLiveData(workflowService.schemaLoading$);
  const hasWorkflow = !!config.workflowId;
  const schemaLoading =
    !!config.workflowId &&
    !workflowSchema &&
    (schemaLoadingMap.get(config.workflowId) ?? true);
  const openWorkflow = useOpenWorkflow(config);

  const handleMappingsChange = useCallback(
    (next: OutputMapping[]) => onUpdate({ outputMappings: next }),
    [onUpdate]
  );
  const handleMatchConfigsChange = useCallback(
    (next: MatchConfigProps[]) => onUpdate({ matchConfigs: next }),
    [onUpdate]
  );
  const handleOutputTablesChange = useCallback(
    (next: OutputTable[]) => onUpdate({ outputTables: next }),
    [onUpdate]
  );
  const handleRowWriteChange = useCallback(
    (next: RowWriteState) => onUpdate(next),
    [onUpdate]
  );

  const { addOutputTable, removeOutputTable, updateOutputTable } =
    useOutputTableActions(config, onUpdate);

  /** The response fields that can hold a list. */
  const listFieldOptions = useMemo(
    () =>
      Object.entries(workflowSchema?.output ?? {})
        .map(([fieldId, fieldDef]) => {
          const def = fieldDef as { name?: string; type?: string };
          return {
            value: fieldId,
            label: def.name || fieldId,
            type: def.type || 'json',
          };
        })
        .filter(field => canHoldList(field.type)),
    [workflowSchema]
  );

  const handleFanoutChange = useCallback(
    (tableId: string, listField: string | null) => {
      onUpdate({
        outputTables: savedTables.map(table => {
          if (table.tableId !== tableId) return table;
          const { sourceArrayField: _previous, ...rest } = table;
          return listField ? { ...rest, sourceArrayField: listField } : rest;
        }),
      });
    },
    [onUpdate, savedTables]
  );

  const selectedTables = savedTables.filter(table => table.tableId);
  const hasMultipleTables = savedTables.length > 1;
  const columnsOf = (table: OutputTable) =>
    tableColumnsMap?.get(table.tableId) ??
    (table.tableId === savedTables[0]?.tableId ? targetColumns : NO_COLUMNS);
  const nameOf = (table: OutputTable) =>
    hasMultipleTables
      ? table.tableName || t['com.streamlain.streamlain.common.untitled']()
      : undefined;
  const selectTargetFirst = (
    <SectionHint>
      {t['com.streamlain.streamlain.output.select-target-first']()}
    </SectionHint>
  );

  return (
    <>
      <SettingSection tile="target" section="target-tables">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.output.target']()}
          subtitle={t['com.streamlain.automation.panel.output-hint']()}
        >
          {savedTables.length === 0 && (
            <SectionHint>
              {t['com.streamlain.automation.panel.no-output']()}
            </SectionHint>
          )}
          {savedTables.map((table, index) => {
            // Get IDs of other selected tables to exclude from dropdown
            const otherSelectedIds = savedTables
              .filter((_, i) => i !== index)
              .map(entry => entry.tableId)
              .filter(Boolean);
            return (
              <SectionTableRow
                key={table.tableId || `table-${index}`}
                spaced={index > 0}
                action={
                  <SectionRemoveButton
                    onClick={() => removeOutputTable(index)}
                    title={t[
                      'com.streamlain.streamlain.output.remove-target-table'
                    ]()}
                  />
                }
              >
                <TableSelector
                  value={table.tableId || undefined}
                  onChange={db => updateOutputTable(index, db)}
                  placeholder={t[
                    'com.streamlain.streamlain.output.select-target-table'
                  ]()}
                  excludeBlockIds={otherSelectedIds}
                  requiredPermission="write"
                />
              </SectionTableRow>
            );
          })}

          <SectionAddLink onClick={addOutputTable}>
            <span>+</span>
            {t['com.streamlain.streamlain.common.add-table']()}
          </SectionAddLink>
        </CollapsibleSection>
      </SettingSection>

      <SettingSection tile="fields" section="field-mapping">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.output.field-mapping']()}
          onInfoClick={() => registry.start('automation:outputMapping')}
        >
          {selectedTables.length === 0
            ? selectTargetFirst
            : selectedTables.map(table => (
                <SectionTableGroup key={table.tableId} name={nameOf(table)}>
                  <FieldMappingGrid
                    tableId={table.tableId}
                    targetColumns={columnsOf(table)}
                    mappings={mappings.filter(
                      mapping => mapping.tableId === table.tableId
                    )}
                    onMappingsChange={next =>
                      // Replace this table's mappings, keep the others.
                      handleMappingsChange([
                        ...mappings.filter(
                          mapping => mapping.tableId !== table.tableId
                        ),
                        ...next,
                      ])
                    }
                    matchConfigs={matchConfigs}
                    onMatchConfigsChange={handleMatchConfigsChange}
                    outputTables={savedTables}
                    onOutputTablesChange={handleOutputTablesChange}
                    sourceColumns={sourceColumns}
                    hasWorkflow={hasWorkflow}
                    schemaLoading={schemaLoading}
                    onOpenWorkflow={openWorkflow}
                  />
                </SectionTableGroup>
              ))}

          {/* sl-3nk: only the owner sets AI matching, once a relation lookup is used */}
          {isOwner && hasRelationLookup && (
            <SectionControlRow
              label={t['com.streamlain.streamlain.output.ai-matching']()}
              description={t[
                'com.streamlain.streamlain.output.ai-matching-subtitle'
              ]()}
            >
              <Switch
                checked={userSettings?.aiRelationMatchingEnabled ?? false}
                disabled={isUpdatingAiSetting}
                onChange={handleAiMatchingToggle}
              />
            </SectionControlRow>
          )}
        </CollapsibleSection>
      </SettingSection>

      <SettingSection tile="match">
        <CollapsibleSection
          title={t['com.streamlain.automation.panel.tile.match']()}
        >
          {selectedTables.length === 0
            ? selectTargetFirst
            : selectedTables.map(table => (
                <SectionTableGroup key={table.tableId} name={nameOf(table)}>
                  <RowWriteSection
                    table={table}
                    columns={columnsOf(table)}
                    mappings={mappings}
                    matchConfigs={matchConfigs}
                    outputTables={savedTables}
                    isSourceTable={isSourceRowTable(config, table)}
                    onChange={handleRowWriteChange}
                  />
                </SectionTableGroup>
              ))}
        </CollapsibleSection>
      </SettingSection>

      <SettingSection tile="fanout">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.output.fanout']()}
        >
          {/* Until a list is split, an example shows what the switch does. */}
          {selectedTables.length > 0 &&
            selectedTables.every(table => !table.sourceArrayField) && (
              <FanoutExample />
            )}
          {selectedTables.length === 0
            ? selectTargetFirst
            : selectedTables.map(table => {
                const listField = table.sourceArrayField ?? null;
                // A saved list field stays selectable even when the workflow
                // no longer returns it.
                const options =
                  listField &&
                  !listFieldOptions.some(option => option.value === listField)
                    ? [
                        ...listFieldOptions,
                        { value: listField, label: listField, type: 'json' },
                      ]
                    : listFieldOptions;
                const noList = !listField && options.length === 0;
                // The processed row is one row: no row per list entry.
                const sourceRow = updatesSourceRow(config, table);
                const listLabel =
                  t['com.streamlain.automation.sidebar.fanout.list-label']();
                return (
                  <SectionTableGroup key={table.tableId} name={nameOf(table)}>
                    <SectionControlRow
                      label={t[
                        'com.streamlain.automation.sidebar.fanout.switch'
                      ]()}
                      description={
                        sourceRow
                          ? t[
                              'com.streamlain.automation.sidebar.fanout.source-row'
                            ]()
                          : noList
                            ? t[
                                'com.streamlain.automation.sidebar.fanout.no-list'
                              ]()
                            : undefined
                      }
                    >
                      <Switch
                        checked={!!listField}
                        disabled={sourceRow || noList}
                        aria-label={t[
                          'com.streamlain.automation.sidebar.fanout.switch'
                        ]()}
                        onChange={checked =>
                          handleFanoutChange(
                            table.tableId,
                            checked ? (options[0]?.value ?? null) : null
                          )
                        }
                      />
                    </SectionControlRow>
                    {listField && (
                      <SectionField
                        label={listLabel}
                        hint={t[
                          'com.streamlain.automation.sidebar.fanout.list-hint'
                        ]()}
                      >
                        <SettingSelect
                          value={listField}
                          options={options}
                          onChange={next =>
                            handleFanoutChange(table.tableId, next)
                          }
                          ariaLabel={listLabel}
                          data-testid="fanout-list-field"
                        />
                      </SectionField>
                    )}
                  </SectionTableGroup>
                );
              })}
        </CollapsibleSection>
      </SettingSection>
    </>
  );
};
